import {
  DEFAULT_WORKFLOW_CONFIG,
  workflowConfigSchema,
  type WorkflowConfig,
} from '@remote-medium/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, ErrorState, unwrap, useApi } from '@remote-medium/ui';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { NumberRow } from './NumberRow.tsx';
import { admin } from '../../../lib/client.ts';
import { Card, SectionHeader } from '../../../lib/ui.tsx';

export function WorkflowSettings() {
  const { data, error } = useApi(() => unwrap(admin['workflow-config'].$get()), []);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const { register, handleSubmit, reset, formState } = useForm<WorkflowConfig>({
    resolver: zodResolver(workflowConfigSchema),
    defaultValues: DEFAULT_WORKFLOW_CONFIG,
  });
  const errors = formState.errors;

  useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);

  if (error) return <ErrorState error={error} />;

  const save = handleSubmit(async (values) => {
    setMsg(null);
    try {
      reset(await unwrap(admin['workflow-config'].$put({ json: values })));
      setMsg({ ok: true, text: 'Saved' });
    } catch {
      setMsg({ ok: false, text: 'Could not save' });
    }
  });

  return (
    <form onSubmit={save}>
      <Card>
        <SectionHeader title="Introductions" />
        <div className="flex flex-col">
          <NumberRow
            id="reactionWindowDays"
            label="Company reaction window"
            hint="After this, you can introduce every consenting candidate."
            unit="days"
            error={errors.reactionWindowDays?.message}
            {...register('reactionWindowDays', { setValueAs: Number })}
          />
          <NumberRow
            id="consentTokenDays"
            label="Consent link validity"
            hint="How long a candidate's yes/no link works."
            unit="days"
            error={errors.consentTokenDays?.message}
            {...register('consentTokenDays', { setValueAs: Number })}
          />
        </div>
        <div className="flex items-center justify-end gap-3">
          {msg && (
            <span className={msg.ok ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}>
              {msg.text}
            </span>
          )}
          <Button type="submit" size="sm" disabled={formState.isSubmitting || !formState.isDirty}>
            {formState.isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Card>
    </form>
  );
}
